import { useEffect, useRef } from 'react';
import { useGeo } from '@/context/GeoContext';

declare global {
  interface Window {
    __SALESPANEL_LOADED?: boolean;
    __loadSalespanel?: () => void;
    $salespanel?: unknown[];
    __sp?: string;
  }
}

/**
 * GeoAwareSalespanel - Loads Salespanel script only after geo-check confirms
 * the user is from an allowed country. This prevents tracking visitors from blocked countries.
 * 
 * For returning visitors with a cached geo-check, index.html will load Salespanel immediately.
 * This component handles first-time visitors without a cache.
 */
export const GeoAwareSalespanel = () => {
  const { geoState, isAnalyticsAllowed } = useGeo();
  const hasTriggeredLoad = useRef(false);

  useEffect(() => {
    // Wait for geo-check to complete
    if (!geoState.isChecked) {
      console.log('[GeoAwareSalespanel] Waiting for geo-check...');
      return;
    }

    // Don't load if already loaded from index.html
    if (window.__SALESPANEL_LOADED) {
      console.log('[GeoAwareSalespanel] Already loaded from index.html');
      return;
    }

    // Don't trigger more than once
    if (hasTriggeredLoad.current) {
      return;
    }

    // Check if analytics is allowed for this country
    if (!isAnalyticsAllowed()) {
      console.log('[GeoAwareSalespanel] Blocked for country:', geoState.countryCode);
      return;
    }

    // Load Salespanel for allowed users
    console.log('[GeoAwareSalespanel] Loading for allowed country:', geoState.countryCode);
    hasTriggeredLoad.current = true;

    if (typeof window.__loadSalespanel === 'function') {
      window.__loadSalespanel();
    } else {
      // Fallback if the global function isn't available
      window.__SALESPANEL_LOADED = true;
      window.$salespanel = window.$salespanel || [];
      window.__sp = "09b39415-0d9f-4a89-8960-35f4c8e171bc";
      const script = document.createElement('script');
      script.src = 'https://salespanel.io/src/js/09b39415-0d9f-4a89-8960-35f4c8e171bc/sp.js';
      script.async = true;
      document.head.appendChild(script);
      console.log('[GeoAwareSalespanel] Script loaded via fallback');
    }
  }, [geoState.isChecked, geoState.countryCode, isAnalyticsAllowed]);

  return null;
};
